import {Link, useLocation} from "react-router-dom"
import {default as cx} from "classnames"

import {getToken, checkIsAdopting} from "../../utilities/useToken"
import {cancelPersona, calculateMetricsRoutes} from "../../utilities/general"
import {NHSR_ROLES, OSR_ROLES} from "../../utilities/roleAccess"
import {METRIC_NAVBAR_LINKS} from "./RouteLinks"

const rowClass =
    "d-flex align-items-center justify-content-between w-100 px-5 py-4 border-bottom text-start text-gray-900 text-hover-primary bg-hover-light"

const SectionCard = ({title, children}) => (
    <div className="px-4 mb-4">
        {title && <div className="fw-bold fs-4 text-gray-900 mb-3">{title}</div>}
        <div className="card border overflow-hidden">
            <div className="card-body p-0">{children}</div>
        </div>
    </div>
)

const NavRow = ({to, testId, title, icon, active}) => (
    <Link to={to} data-testid={testId} className={cx("menu-item", rowClass, {"here show": active})}>
        <span className="d-flex align-items-center">
            <i className={cx("fas fs-3 text-primary me-4", icon)}></i>
            <span className="fw-semibold">{title}</span>
        </span>
        <i className="fas fa-chevron-right fs-7 text-gray-500"></i>
    </Link>
)

const HelpRow = ({href, icon, title}) => (
    <a href={href} className={rowClass}>
        <span className="d-flex align-items-center">
            <i className={cx("fas fs-3 text-primary me-4", icon)}></i>
            <span className="fw-semibold">{title}</span>
        </span>
        <i className="fas fa-chevron-right fs-7 text-gray-500"></i>
    </a>
)

const MobileDropdownMenu = ({setDarkMode, darkMode, fullPage = false}) => {
    const location = useLocation()
    const {role, display_name: userAbbr, user_name: userName} = getToken()
    const isEnterpriseAdmin = role === "Enterprise Admin"
    const showAdmin = ["Enterprise Admin", "Administrator", "Educator"].includes(role)
    const metricLinks = calculateMetricsRoutes(METRIC_NAVBAR_LINKS, role)
    const isAdopting = checkIsAdopting()
    const nonProd = window.__APP_CONFIG__?.ENV && window.__APP_CONFIG__.ENV !== "prod"

    return (
        <div
            className={cx(
                "d-flex menu menu-column menu-sub menu-rounded fw-semibold fs-6 py-4",
                fullPage ? "w-100" : "menu-sub-dropdown w-300px mw-100 mobile-nav-scroll"
            )}
        >
            <div className="d-flex align-items-center px-5 pb-4">
                <div className="symbol symbol-40px me-4">
                    <div className="symbol-label fs-3 fw-semibold bg-primary text-white">{userAbbr}</div>
                </div>
                <div className="d-flex flex-column">
                    {role && <div className="badge badge-primary fw-bold fs-8 mb-1 align-self-start">{role}</div>}
                    <div className="fw-bold fs-5 text-gray-900">{userName}</div>
                </div>
            </div>

            <SectionCard title="Menu">
                <NavRow
                    to="/home"
                    testId="nav-home-link"
                    title="Home"
                    icon="fa-house"
                    active={location.pathname.startsWith("/home")}
                />
                {role === "Preceptor" && (
                    <>
                        {/* <NavRow
                            to="/preceptor-metrics"
                            testId="nav-preceptor-metrics-link"
                            title="My Metrics"
                            icon="fa-hospital-user"
                            active={location.pathname.startsWith("/preceptor-metrics")}
                        /> */}
                        <NavRow
                            to="/preceptor-orientations"
                            testId="nav-preceptor-orientations-link"
                            title="My Orientees"
                            icon="fa-user-nurse"
                            active={location.pathname.startsWith("/preceptor-orientations")}
                        />
                        <NavRow
                            to="/preceptor-completed-orientations"
                            testId="nav-preceptor-completed-orientations-link"
                            title="Archived Orientees"
                            icon="fa-box-archive"
                            active={location.pathname.startsWith("/preceptor-completed-orientations")}
                        />
                    </>
                )}
                {["Administrator", "Educator", "Enterprise Admin"].includes(role) && (
                    <>
                        <NavRow
                            to="/educator-onboarding"
                            testId="nav-educator-onboarding-link"
                            title="Onboard Orientees"
                            icon="fa-list-check"
                            active={location.pathname.startsWith("/educator-onboarding")}
                        />
                        <NavRow
                            to="/educator-orientations"
                            testId="nav-educator-active-orientees-link"
                            title="Active Orientees"
                            icon="fa-user-nurse"
                            active={
                                location.pathname.startsWith("/educator-orientations") &&
                                !location.search.includes("archived=true")
                            }
                        />
                        <NavRow
                            to="/educator-orientations?archived=true"
                            testId="nav-educator-archived-orientees-link"
                            title="Archived Orientees"
                            icon="fa-clipboard-user"
                            active={
                                location.pathname.startsWith("/educator-orientations") &&
                                location.search.includes("archived=true")
                            }
                        />
                    </>
                )}
                {[...NHSR_ROLES, ...OSR_ROLES].includes(role) && role !== "Administrator" && (
                    <>
                        <NavRow
                            to="/surveys/new-hire-status-report-selection"
                            testId="nav-new-hire-status-link"
                            title="New Hire Status Report"
                            icon="fa-clipboard-list"
                            active={location.pathname.startsWith("/surveys/new-hire-status-report-selection")}
                        />
                        <NavRow
                            to="/surveys/orientation-status-report-selection"
                            testId="nav-orientation-status-report-link"
                            title="Orientation Status Report"
                            icon="fa-clipboard-check"
                            active={location.pathname.startsWith("/surveys/orientation-status-report")}
                        />
                    </>
                )}
                {metricLinks.map((route) => (
                    <NavRow
                        key={route.key}
                        to={route.link}
                        testId={`nav-metrics-${route.key}-link`}
                        title={route.name}
                        icon={route.icon}
                        active={location.pathname.startsWith(route.link)}
                    />
                ))}
                {isAdopting && nonProd && (
                    <button type="button" onClick={cancelPersona} className={cx("btn rounded-0", rowClass)}>
                        <span className="d-flex align-items-center">
                            <i className="fas fa-rotate-left fs-3 text-primary me-4"></i>
                            <span className="fw-semibold">Cancel Persona</span>
                        </span>
                    </button>
                )}
            </SectionCard>

            {showAdmin && (
                <SectionCard title="Administration">
                    {isEnterpriseAdmin && (
                        <NavRow
                            to="/admin/organization"
                            testId="nav-admin-organizations-link"
                            title="Organizations"
                            icon="fa-sitemap"
                            active={location.pathname.startsWith("/admin/organization")}
                        />
                    )}
                    <NavRow
                        to="/admin/users"
                        testId="nav-admin-users-link"
                        title="User Accounts"
                        icon="fa-users"
                        active={location.pathname.startsWith("/admin/users")}
                    />
                    {isEnterpriseAdmin && nonProd && (
                        <NavRow
                            to="/adopt-persona"
                            testId="nav-adopt-persona-link"
                            title="Adopt Persona"
                            icon="fa-user-secret"
                            active={location.pathname.startsWith("/adopt-persona")}
                        />
                    )}
                </SectionCard>
            )}

            <SectionCard title="Help & Support">
                {/* TODO(OARS-477): replace placeholder hrefs once User Guide/Training/Release Notes destinations exist. */}
                <HelpRow href="#" icon="fa-signs-post" title="User Guide" />
                <HelpRow href="#" icon="fa-circle-play" title="Training Videos" />
                <HelpRow href="#" icon="fa-circle-info" title="Release Notes" />
                <HelpRow
                    href="mailto:UMMS-OARS-support@umm.edu?subject=Support Request"
                    icon="fa-envelope"
                    title="Contact Support"
                />
            </SectionCard>

            <SectionCard title="Screen Mode">
                <button
                    type="button"
                    onClick={() => setDarkMode(false)}
                    className={cx("btn rounded-0", rowClass, {"active bg-light-primary text-primary": !darkMode})}
                >
                    <span className="d-flex align-items-center">
                        <i className="fas fa-sun fs-3 me-4"></i>
                        <span className="fw-semibold">Light</span>
                    </span>
                </button>
                <button
                    type="button"
                    onClick={() => setDarkMode(true)}
                    className={cx("btn rounded-0", rowClass, {"active bg-light-primary text-primary": !!darkMode})}
                >
                    <span className="d-flex align-items-center">
                        <i className="fas fa-moon fs-3 me-4"></i>
                        <span className="fw-semibold">Dark</span>
                    </span>
                </button>
            </SectionCard>

            <div className="px-4">
                <a
                    href="/logoutnav"
                    data-testid="nav-logout-link"
                    className="btn btn-primary w-100 py-3 hover-elevate-down"
                >
                    Log Out
                </a>
            </div>
        </div>
    )
}

export default MobileDropdownMenu
